import { DatePipe, NgClass } from '@angular/common';
import { Component, computed, input, linkedSignal, model, output } from '@angular/core';
import { FormValueControl } from '@angular/forms/signals';
import { NgIcon } from '@ng-icons/core';
import { eachDayOfInterval, endOfWeek, addMonths } from 'date-fns';
import { DateString, getSixWeekRange, getWeekDays } from './definitions';
import { fromDateString, toDateString } from '../calendar-range-input/definitions';
import { Button } from '../button/button';

@Component({
  selector: 'ui-calendar-input',
  imports: [DatePipe, Button, NgIcon, NgClass],
  templateUrl: './calendar-input.html',
})
export class CalendarInput implements FormValueControl<DateString | null> {
  readonly value = model<DateString | null>(null);
  readonly disabled = input<boolean>(false);
  readonly touched = model<boolean>(false);

  fieldValueChange = output<DateString | null>();

  protected month = linkedSignal(() => this.value() ?? toDateString(new Date()));

  protected weekDays = computed(getWeekDays);

  private weekRange = computed(() => getSixWeekRange(this.month()));

  protected weeksOfMonth = computed(() => {
    const month = this.month();
    const weeks = this.weekRange();

    const currentMonth = fromDateString(month).getMonth();
    const value = this.value();
    return weeks.map((week) =>
      eachDayOfInterval({
        start: week,
        end: endOfWeek(week, { weekStartsOn: 1 }),
      }).map((nativeDate) => {
        const date = toDateString(nativeDate);
        if (date === value) {
          return { date, status: 'selected' };
        } else if (nativeDate.getMonth() === currentMonth) {
          return { date, status: 'inRange' };
        } else {
          return { date, status: 'outOfRange' };
        }
      }),
    );
  });

  onClickNext() {
    this.month.set(toDateString(addMonths(this.month(), 1)));
  }

  onClickPrevious() {
    this.month.set(toDateString(addMonths(this.month(), -1)));
  }

  onClickDay(day: DateString) {
    this.value.set(day);
    this.touched.set(true);
    this.fieldValueChange.emit(day);
  }
}
